import React from 'react';
import { Box, Typography, Button, Container, Grid } from '@mui/material';
import { motion, Variants } from 'framer-motion';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const MINI_STATS = [
  { value: '+20', label: 'eventos' },
  { value: '95%', label: 'satisfacción' },
  { value: '3', label: 'formatos' },
];

const stagger: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.15 } },
};

const ease = [0.16, 1, 0.3, 1] as [number, number, number, number];

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease } },
};

interface Props {
  onOpenContact: () => void;
}

const Hero: React.FC<Props> = ({ onOpenContact }) => {
  return (
    <Box
      id="hero"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        background: '#0d0d0d',
      }}
    >
      {/* Radial warm glow — bottom right */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(ellipse 70% 60% at 80% 110%, rgba(200,169,110,0.09) 0%, transparent 70%), radial-gradient(ellipse 50% 40% at 20% -10%, rgba(200,55,45,0.05) 0%, transparent 60%)',
          pointerEvents: 'none',
        }}
      />

      {/* Top accent line */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: 'linear-gradient(90deg, transparent 0%, var(--color-accent) 50%, transparent 100%)',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, pt: { xs: 14, md: 10 }, pb: 6 }}>
        <Grid container spacing={4} sx={{ alignItems: 'center' }}>

          {/* ── Left column: text ── */}
          <Grid size={{ xs: 12, md: 7 }}>
            <motion.div variants={stagger} initial="hidden" animate="show">

              <motion.div variants={fadeUp}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                  <Box sx={{ width: 32, height: 1, bgcolor: 'var(--color-accent)' }} />
                  <Typography
                    variant="overline"
                    sx={{ color: 'var(--color-accent)', letterSpacing: '0.35em', fontSize: '0.75rem' }}
                  >
                    Catering de sushi premium
                  </Typography>
                </Box>
              </motion.div>

              <motion.div variants={fadeUp}>
                <Typography
                  variant="h1"
                  sx={{
                    fontFamily: 'var(--font-heading)',
                    fontSize: { xs: '2.6rem', sm: '3.2rem', md: '3.8rem', lg: '4.6rem' },
                    fontWeight: 700,
                    lineHeight: 1.08,
                    color: 'var(--color-text-primary)',
                    mb: 3,
                    letterSpacing: '-0.01em',
                  }}
                >
                  Sushi en vivo{' '}
                  <Box
                    component="span"
                    sx={{
                      color: 'var(--color-accent)',
                      fontStyle: 'italic',
                    }}
                  >
                    para eventos
                  </Box>
                  {' '}que no se olvidan
                </Typography>
              </motion.div>

              <motion.div variants={fadeUp}>
                <Typography
                  sx={{
                    color: 'var(--color-text-secondary)',
                    fontSize: { xs: '1rem', md: '1.1rem' },
                    lineHeight: 1.8,
                    mb: 5,
                    maxWidth: 520,
                  }}
                >
                  Llevamos la gastronomía japonesa más auténtica a tu celebración.
                  Un sushiman profesional elabora en directo, transforma tu espacio
                  y convierte cada bocado en un recuerdo.
                </Typography>
              </motion.div>

              <motion.div variants={fadeUp}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 6 }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={onOpenContact}
                    sx={{
                      bgcolor: 'var(--color-accent)',
                      color: '#0d0d0d',
                      fontWeight: 700,
                      px: 4,
                      py: 1.6,
                      fontSize: '0.95rem',
                      letterSpacing: '0.03em',
                      borderRadius: '4px',
                      boxShadow: '0 0 24px rgba(200,169,110,0.25)',
                      '&:hover': {
                        bgcolor: 'var(--color-accent-hover)',
                        boxShadow: '0 0 32px rgba(200,169,110,0.4)',
                      },
                      transition: 'all 0.25s',
                    }}
                  >
                    Solicitar información
                  </Button>
                  <Button
                    component="a"
                    href="#services"
                    variant="outlined"
                    size="large"
                    sx={{
                      borderColor: 'var(--color-border-hover)',
                      color: 'var(--color-text-secondary)',
                      px: 4,
                      py: 1.6,
                      fontSize: '0.95rem',
                      borderRadius: '4px',
                      '&:hover': {
                        borderColor: 'var(--color-accent)',
                        color: 'var(--color-accent)',
                        bgcolor: 'var(--color-accent-muted)',
                      },
                      transition: 'all 0.25s',
                    }}
                  >
                    Ver servicios
                  </Button>
                </Box>
              </motion.div>

              {/* Mini stats */}
              <motion.div variants={fadeUp}>
                <Box sx={{ display: 'flex', gap: 0, alignItems: 'stretch' }}>
                  {MINI_STATS.map((s, i) => (
                    <Box
                      key={s.label}
                      sx={{
                        px: 3,
                        borderLeft: i === 0 ? '1px solid var(--color-border)' : 'none',
                        borderRight: '1px solid var(--color-border)',
                        '&:first-of-type': { pl: 0, borderLeft: 'none' },
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'var(--font-heading)',
                          fontSize: '1.6rem',
                          fontWeight: 700,
                          color: 'var(--color-text-primary)',
                          lineHeight: 1,
                        }}
                      >
                        {s.value}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em' }}
                      >
                        {s.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </motion.div>

            </motion.div>
          </Grid>

          {/* ── Right column: decorative SVG ── */}
          <Grid size={{ xs: 12, md: 5 }} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center', alignItems: 'center' }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.85, rotate: -10 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.4 }}
              style={{ position: 'relative', width: 400, height: 400 }}
            >
              {/* Slow-rotating outer ring */}
              <motion.svg
                viewBox="0 0 400 400"
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
                animate={{ rotate: 360 }}
                transition={{ duration: 80, repeat: Infinity, ease: 'linear' }}
              >
                <circle cx="200" cy="200" r="190" fill="none" stroke="#c8a96e" strokeWidth="0.6" strokeOpacity="0.2" strokeDasharray="6 14" />
              </motion.svg>

              {/* Static middle ring */}
              <svg viewBox="0 0 400 400" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                <circle cx="200" cy="200" r="150" fill="none" stroke="#c8a96e" strokeWidth="1" strokeOpacity="0.15" />
              </svg>

              {/* Enso brushstroke-style arc */}
              <svg viewBox="0 0 400 400" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                <circle
                  cx="200" cy="200" r="130"
                  fill="none"
                  stroke="#c8a96e"
                  strokeWidth="18"
                  strokeOpacity="0.18"
                  strokeLinecap="round"
                  strokeDasharray="720 90"
                  transform="rotate(-100 200 200)"
                />
              </svg>

              {/* Inner spiral arms — simplified Gi emblem */}
              <svg viewBox="0 0 400 400" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                <path
                  d="M200,200 C200,160 240,140 260,160 C280,180 270,220 240,230 C210,240 180,220 175,195 C170,165 190,145 215,145"
                  fill="none"
                  stroke="#c8a96e"
                  strokeWidth="2.5"
                  strokeOpacity="0.5"
                  strokeLinecap="round"
                />
                <path
                  d="M200,200 C200,240 160,260 140,240 C120,220 130,180 160,170 C190,160 220,180 225,205 C230,235 210,255 185,255"
                  fill="none"
                  stroke="#c8a96e"
                  strokeWidth="2.5"
                  strokeOpacity="0.5"
                  strokeLinecap="round"
                />
                {/* Red dot — the signature mark from the logo */}
                <circle cx="200" cy="200" r="6" fill="#c8372d" fillOpacity="0.85" />
              </svg>
            </motion.div>
          </Grid>

        </Grid>
      </Container>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.6 }}
        style={{ position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)' }}
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        >
          <Box component="a" href="#intro" sx={{ color: 'var(--color-text-secondary)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, textDecoration: 'none', '&:hover': { color: 'var(--color-accent)' }, transition: 'color 0.2s' }}>
            <Typography variant="caption" sx={{ letterSpacing: '0.2em', textTransform: 'uppercase', fontSize: '0.65rem' }}>
              Scroll
            </Typography>
            <KeyboardArrowDownIcon sx={{ fontSize: 20 }} />
          </Box>
        </motion.div>
      </motion.div>
    </Box>
  );
};

export default Hero;
